import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Select,
  DatePicker,
  Button,
  Typography,
  Alert,
  Space,
  Statistic
} from 'antd';
import { SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchActiveBarns, fetchDeadChickenReport } from '../utils/deadChicken';

/** 進入報表預設查詢天數（各雞舍近 N 天） */
const DEFAULT_REPORT_DAYS = 10;

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function formatDate(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD');
}

function formatDateTime(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

const DeadChickenReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [barns, setBarns] = useState([]);
  const [barnId, setBarnId] = useState(null);
  const [zone, setZone] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(DEFAULT_REPORT_DAYS - 1, 'day'),
    dayjs()
  ]);

  useEffect(() => {
    fetchActiveBarns()
      .then(setBarns)
      .catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [from, to] = dateRange || [];
      const data = await fetchDeadChickenReport({
        barnId: barnId || undefined,
        zone: barnId ? undefined : zone || undefined,
        dateFrom: from ? formatDate(from) : undefined,
        dateTo: to ? formatDate(to) : undefined
      });

      setRecords(data.records || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setRecords([]);
      setTotalCount(0);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [barnId, zone, dateRange]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleBarnChange = (value) => {
    setBarnId(value || null);
    if (value) {
      setZone(null);
    }
  };

  const handleZoneChange = (value) => {
    setZone(value || null);
    if (value) {
      setBarnId(null);
    }
  };

  const columns = [
    {
      title: t('report.deadChicken.date'),
      dataIndex: 'add_datetime',
      key: 'add_datetime',
      render: (value) => formatDateTime(value),
      width: 160
    },
    {
      title: t('report.deadChicken.barn'),
      dataIndex: 'barn_id',
      key: 'barn_id',
      width: 90
    },
    {
      title: t('report.deadChicken.barnName'),
      dataIndex: 'barn_name',
      key: 'barn_name',
      responsive: ['md']
    },
    {
      title: t('report.deadChicken.count'),
      dataIndex: 'employee_count',
      key: 'employee_count',
      align: 'right',
      width: 100
    },
    {
      title: t('report.deadChicken.recordedBy'),
      dataIndex: 'add_uid',
      key: 'add_uid',
      responsive: ['lg'],
      width: 120
    }
  ];

  return (
    <div className="report-page max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
      <Link to="/reports" className="inline-flex items-center text-wabi-brown-700 hover:text-wabi-brown-900">
        <ArrowLeftOutlined className="mr-1" />
        {t('report.back')}
      </Link>

      <Title level={3} className="!mb-0 !text-wabi-brown-800">
        {t('report.menu.dead-chicken')}
      </Title>

      <Alert
        type="info"
        showIcon
        message={t('report.deadChicken.defaultRangeTitle')}
        description={t('report.deadChicken.defaultRangeNote', { days: DEFAULT_REPORT_DAYS })}
      />

      <Card className="bg-white/90 border-0 shadow-sm">
        <Space direction="vertical" size="middle" className="w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Text className="block text-sm text-gray-500 mb-1">
                {t('report.deadChicken.dateRange')}
              </Text>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full"
                allowClear={false}
              />
            </div>
            <div>
              <Text className="block text-sm text-gray-500 mb-1">
                {t('report.deadChicken.zone')}
              </Text>
              <Select
                allowClear
                placeholder={t('report.deadChicken.zoneAll')}
                value={zone}
                onChange={handleZoneChange}
                disabled={Boolean(barnId)}
                className="w-full"
                options={['A', 'B', 'C'].map((z) => ({
                  value: z,
                  label: t('home.map.zone', { zone: z })
                }))}
              />
            </div>
            <div>
              <Text className="block text-sm text-gray-500 mb-1">
                {t('report.deadChicken.barn')}
              </Text>
              <Select
                allowClear
                showSearch
                placeholder={t('report.deadChicken.barnAll')}
                value={barnId}
                onChange={handleBarnChange}
                disabled={Boolean(zone)}
                className="w-full"
                optionFilterProp="label"
                options={barns.map((b) => ({
                  value: b.barn_id,
                  label: b.barn_id
                }))}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={loadReport}
                loading={loading}
                block
              >
                {t('report.deadChicken.search')}
              </Button>
            </div>
          </div>

          <Statistic
            title={t('report.deadChicken.total')}
            value={totalCount}
            className="!mt-2"
          />
        </Space>
      </Card>

      {error && <Alert type="error" message={error} showIcon />}

      <Card className="bg-white/90 border-0 shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('report.deadChicken.rowTotal', { total })
          }}
          scroll={{ x: 480 }}
          locale={{ emptyText: t('report.deadChicken.empty') }}
        />
      </Card>
    </div>
  );
};

export default DeadChickenReport;
